import JwtToken from "./JwtToken";

const API_ENDPOINT = "";

type CallArguments = {
  method: unknown;
  resource: unknown;
  query?: Record<string, unknown>;
  body?: unknown;
  asBlob?: boolean;
};

class Api {
  static singletonInstance?: Api;
  static singleton(): Api {
    return (
      this.singletonInstance ||
      (this.singletonInstance = new this(JwtToken.singleton()))
    );
  }

  jwtToken: JwtToken;

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private constructor(jwtToken: JwtToken) {
    this.jwtToken = jwtToken;
  }

  async fetchWithCredential(
    input: RequestInfo,
    init?: RequestInit | undefined
  ) {
    await this.jwtToken.tryLoadJwtToken();
    const initWithCredential = init || ({} as RequestInit);
    initWithCredential.credentials = "same-origin";
    initWithCredential.headers = (initWithCredential.headers || {}) as Record<
      string,
      string
    >;
    if (this.jwtToken.currentTokenString) {
      initWithCredential.headers["Authorization"] =
        "Bearer " + this.jwtToken.currentTokenString;
    } else {
      delete initWithCredential.headers["Authorization"];
    }

    return await fetch(input, initWithCredential);
  }

  async uploadFile(file: File): Promise<void> {
    //const body = new FormData();
    //body.append("file", file);
    const res = await this.fetchWithCredential(
      API_ENDPOINT + "/api/adv/media_objects",
      {
        method: "POST",
        body: file,
        headers: {
          "Content-Type": "application/octet-stream",
          "X-File-Type": file.type,
          "X-File-Name": encodeURIComponent(file.name)
        }
      }
    );
    if (!res.ok) {
      const ret: {
        "@context": string;
        "@type": string;
        "hydra:description": string;
        "hydra:title": string;
        [key: string]: unknown;
      } = await res.json();
      if (ret["@type"] === "ConstraintViolationList") {
        throw ret.violations;
      } else {
        throw [
          { propertyPath: "file", message: "アップロードに失敗しました。" },
        ];
      }
    }
    return await res.json();
  }

  async call({ method, resource, query, body, asBlob = false }: CallArguments) {
    const qs = query
      ? Object.entries(query)
          .map((e) => `${e[0]}=` + encodeURIComponent("" + e[1]))
          .join("&")
      : "";
    body = JSON.stringify(body);
    const init = {
      method,
      body,
      headers: {
        "Content-Type":
          method === "PATCH"
            ? "application/merge-patch+json"
            : "application/json",
      },
    } as RequestInit;

    const res = await this.fetchWithCredential(
      API_ENDPOINT + resource + (qs.length ? "?" + qs : ""),
      init
    );
    if (!res.ok) {
      const ret: {
        "@context": string;
        "@type": string;
        "hydra:description": string;
        "hydra:title": string;
        [key: string]: unknown;
        res: Response | undefined;
      } = await res.json();
      ret.res = res;
      if (ret["@type"] === "ConstraintViolationList") {
        throw ret.violations;
      }
      return ret;
    } else {
      if (asBlob) {
        return await res.blob();
      } else {
        if (res.status === 204) {
          return true;
        }
        return await res.json();
      }
    }
  }

  downloadAs(blob: Blob, name: string) {
    if (window.navigator.msSaveBlob) {
      window.navigator.msSaveBlob(blob, name);
    } else {
      const downloadUrl = (window.URL || window.webkitURL).createObjectURL(
        blob
      );
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = name;
      link.click();
      (window.URL || window.webkitURL).revokeObjectURL(downloadUrl);
    }
  }
}

export default Api;
